import React, { useState, useEffect } from 'react'
import { Form, Input, Button, message } from 'antd'
import { UserOutlined, LockOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import api from '../../utils/api'
import { useNavigate } from 'react-router-dom'
import { App } from 'antd'
import { useAuth } from '../../hooks/AuthContext'

const LoginPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #e8e8e8 0%, #d4c4b0 50%, #c4b5c0 100%);
  padding: 20px;

  @media (max-width: 768px) {
    padding: 20px 16px;
    box-sizing: border-box;
  }
`

const FormContainer = styled.div`
  background: #faf9f6;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(154, 163, 175, 0.2);
  width: 100%;
  max-width: 450px;
  text-align: center;
  border: 1px solid rgba(168, 181, 192, 0.3);

  @media (max-width: 768px) {
    padding: 30px 20px;
    width: 100%;
    max-width: 100%;
    margin: 0;
    box-sizing: border-box;
  }
`

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 10px;
  color: #5a5a5a;
  font-weight: 500;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const Subtitle = styled.p`
  color: #8a8a8a;
  font-size: 16px;
  margin-bottom: 30px;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 25px;
  }
`

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 20px;
  }

  .ant-input-affix-wrapper {
    border: 1.5px solid #d4c4b0;
    border-radius: 12px;
    padding: 12px 16px;
    background-color: #ffffff;
    transition: all 0.3s ease;
    font-size: 16px;

    &:hover {
      border-color: #a8b5c0;
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: #a8b5c0;
      box-shadow: 0 0 0 3px rgba(168, 181, 192, 0.15);
    }

    .ant-input {
      color: #4a4a4a;
      font-size: 16px;

      &::placeholder {
        color: #b8b8b8;
      }
    }

    .anticon {
      color: #8a8a8a;
    }

    @media (max-width: 768px) {
      padding: 10px 14px;
      font-size: 15px;
    }
  }

  .ant-input-password {
    .ant-input {
      &::placeholder {
        color: #b8b8b8;
      }
    }
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #a8b5c0 0%, #9ca3af 100%);
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  margin-top: 10px;
  box-shadow: 0 2px 8px rgba(154, 163, 175, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(156, 163, 175, 0.3);
    background: linear-gradient(135deg, #9ca3af 0%, #8a939f 100%);
    border: none;
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    height: 44px;
    font-size: 16px;
  }
`

const ErrorMessage = styled.p`
  color: #c89b9b;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
  font-weight: 400;
`

const Footer = styled.p`
  color: #9a9a9a;
  font-size: 14px;
  margin-top: 20px;
  text-align: center;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const AdminLoginPage = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { message } = App.useApp()
  const { checkAdminStatus, user } = useAuth()

  useEffect(() => {
    if (user?.role === 'admin' && user?.platformAdminTier === 'staff') {
      navigate('/register', { replace: true })
    }
  }, [user, navigate])

  const onFinish = async (values) => {
    setLoading(true)
    setError('')
    try {
      const response = await api.post('/api/auth/login/admin', values, {
        withCredentials: true
      })
      if (response.data.success) {
        const tier =
          response.data.platformAdminTier === 'staff' ? 'staff' : 'super'
        await checkAdminStatus()
        if (tier === 'staff') {
          message.success('登入成功，子管理員請使用商家註冊流程')
          navigate('/register', { replace: true })
        } else {
          message.success('登入成功')
          navigate('/admin/dashboard')
        }
      } else {
        setError(response.data.message || '登入失敗')
        message.error('登入失敗')
      }
    } catch (error) {
      console.error('Login error:', error)
      setError(error.response?.data?.message || '登入失敗，請重試')
      message.error('登入失敗')
    } finally {
      setLoading(false)
    }
  }

  return (
    <LoginPageContainer>
      <FormContainer>
        <Title>系統管理員登入</Title>
        <Subtitle>管理後台專用登入頁面</Subtitle>
        <StyledForm name='admin_login' onFinish={onFinish} layout='vertical'>
          <Form.Item
            name='username'
            rules={[
              { required: true, message: '請輸入用戶名' },
              {
                pattern: /^[a-zA-Z0-9._]{1,20}$/,
                message: '用戶名必須是1-20個字符，僅可包含英文、數字、底線和點'
              }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder='管理員帳號'
              size='large'
            />
          </Form.Item>
          <Form.Item
            name='password'
            rules={[
              { required: true, message: '請輸入密碼' },
              { min: 6, message: '密碼長度至少為6個字符' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder='密碼'
              size='large'
            />
          </Form.Item>
          <Form.Item>
            <StyledButton
              type='primary'
              htmlType='submit'
              loading={loading}
              block
            >
              登入
            </StyledButton>
          </Form.Item>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </StyledForm>
        <Footer>美感 | BeauteFeel</Footer>
      </FormContainer>
    </LoginPageContainer>
  )
}

export default AdminLoginPage
